import type { Command, JsonValue, RecordDefinition, Transaction } from "@e308/core";
import { rejectUnlessActiveBorder } from "./border-guards.js";
import { rejectIfEliminated } from "./command-guards.js";
import type { SeatModel } from "./content-types.js";
import type { Micro } from "./exact-numbers.js";
import type { MatchLifecycleState } from "./lifecycle-types.js";
import { addTroops, EMPTY_TROOPS } from "./logistics-math.js";
import { homeTroops, setHomeTroops } from "./logistics-simulation.js";
import type { LogisticsState } from "./logistics-types.js";
import { UNIT_IDS } from "./logistics-types.js";
import { launchGathered } from "./reinforcement-runtime.js";
import { initialReinforcement, validateShares } from "./reinforcement-state.js";
import { effectMultiplier, effectsForResearch } from "./research-effects.js";
import type { ConflictIntent } from "./types.js";
import { restartWaveTimer, setWaveTimer } from "./wave-timing.js";

export type ReinforcementIntent = Extract<
  ConflictIntent,
  {
    kind:
      | "route-production"
      | "set-dispatch-mode"
      | "launch-wave"
      | "release-wave"
      | "set-wave-interval";
  }
>;

export function reinforcementCommandFor(
  model: SeatModel,
  intent: ReinforcementIntent,
  stepMs: number,
  seats: ReadonlyMap<string, SeatModel>,
  lifecycle: RecordDefinition<MatchLifecycleState & JsonValue>,
): Command<Micro> {
  return {
    id: `intent:${model.seat.id}:reinforcement`,
    execute(tx: Transaction<Micro>) {
      rejectIfEliminated(tx, model);
      let state = tx.getRecord(model.logistics) as LogisticsState;
      const active = (id: string) => {
        rejectUnlessActiveBorder(tx, model, requiredTarget(seats, id), lifecycle);
      };
      const reinforcement = state.reinforcement ?? initialReinforcement();
      if (intent.kind === "route-production") {
        if (intent.unit !== "all" && !UNIT_IDS.includes(intent.unit))
          throw new TypeError("Unknown troop type");
        const shares = validateShares(intent.shares, Object.keys(state.borders));
        for (const id of Object.keys(shares)) active(id);
        state = {
          ...state,
          autoReinforce: [],
          reinforcement: {
            ...reinforcement,
            shares: Object.fromEntries(
              UNIT_IDS.map((unit) => [
                unit,
                intent.unit === "all" || intent.unit === unit ? shares : reinforcement.shares[unit],
              ]),
            ) as typeof reinforcement.shares,
          },
        };
      } else {
        active(intent.borderId);
        state = { ...state, autoReinforce: [], reinforcement };
        if (intent.kind === "set-wave-interval") {
          state = {
            ...state,
            reinforcement: setWaveTimer(
              reinforcement,
              intent.borderId,
              intent.seconds,
              tx.gameTimeMs(),
            ),
          };
        } else if (intent.kind === "set-dispatch-mode") {
          if (intent.mode !== "gather" && intent.mode !== "continuous")
            throw new TypeError("Invalid dispatch mode");
          state = {
            ...state,
            reinforcement: {
              ...restartWaveTimer(reinforcement, intent.borderId, tx.gameTimeMs()),
              modes: { ...reinforcement.modes, [intent.borderId]: intent.mode },
            },
          };
        } else if (intent.kind === "launch-wave") {
          const multiplier = effectMultiplier(
            effectsForResearch(tx.getRecord(model.research)),
            "military-travel-time",
          );
          state = launchGathered(state, intent.borderId, tx.gameTimeMs(), multiplier, stepMs);
        } else {
          setHomeTroops(
            tx,
            model.resources,
            addTroops(
              homeTroops(tx, model.resources),
              reinforcement.gathered[intent.borderId] ?? EMPTY_TROOPS,
            ),
          );
          state = {
            ...state,
            reinforcement: {
              ...reinforcement,
              gathered: { ...reinforcement.gathered, [intent.borderId]: EMPTY_TROOPS },
            },
          };
        }
      }
      tx.setRecord(model.logistics, state as LogisticsState & JsonValue);
    },
  };
}

function requiredTarget(seats: ReadonlyMap<string, SeatModel>, id: string): SeatModel {
  const target = seats.get(id);
  if (!target) throw new TypeError("Unknown border");
  return target;
}
